import { useState, useEffect } from "react";
import { checkConnection, useNetworkStatus } from "./useNetworkStatus";

export interface ConnectionGuard {
  /** Pass directly to <NoConnectionOverlay open={...} /> */
  open: boolean;
  /** Pass directly to <NoConnectionOverlay onOpenChange={...} /> */
  onOpenChange: (open: boolean) => void;
  /**
   * Call this before any action that requires connectivity.
   * Returns `true` if online (safe to proceed), `false` if offline
   * (the overlay will reappear automatically).
   *
   * @example
   * const onSelectHandle = async () => {
   *   if (!await guardedCheckConnection()) return;
   *   // ... proceed with the action
   * };
   */
  guardedCheckConnection: () => Promise<boolean>;
}

/**
 * Owns the overlay open/closed state and exposes `guardedCheckConnection`
 * that re-opens the overlay whenever the device is still offline —
 * even when the overlay was previously dismissed by the user.
 *
 * Use this in the parent component that also renders `<NoConnectionOverlay>`.
 */
export function useConnectionGuard(): ConnectionGuard {
  const { isOnline } = useNetworkStatus();
  const [open, setOpen] = useState<boolean>(false);

  // Open when the browser passively detects going offline
  useEffect(() => {
    if (!isOnline) setOpen(true);
  }, [isOnline]);

  const guardedCheckConnection = async (): Promise<boolean> => {
    const online = await checkConnection();
    if (!online) {
      // Re-open directly — isOnline is already false so the effect above
      // won't fire again (no state change to trigger it)
      setOpen(true);
    }
    return online;
  };

  return { open, onOpenChange: setOpen, guardedCheckConnection };
}
